/**
 * 261029 P3 (F1 + F2) — đọc SỚM cho bước Tài sản. Không antd, không React ⇒ test được dưới jest.
 *
 * F1: khi biết bước đầu là ASSETS (`?step=`, heuristic từ events, hoặc ngay khi status về) bắn CÙNG LÚC mọi GET Tài sản không phụ thuộc nhau
 *     (7 GET `useBankPrep`, plans của truyện, plan, location-needs, assets/preview ≡ ASSETS/subjects, style-profile, world-setting, world-setting/stale, image-studio/models).
 *     Kết quả vào CHỖ GIAO một lần (`workflowReadHandoff.ts`: epoch ghi + generation + TTL); component mount sau lấy bằng `takeWorkflowHandoff(KEY…)`.
 * F2: ảnh hướng của bối cảnh (`locations/:id/views`, `location-directions/:loc`, `location-views?locationId=`) KHÔNG chờ `location-needs`: ngay khi biết id bối cảnh THUỘC plan
 *     (`bank-usage.locationIds` ∩ locations của truyện) thì bắn đọc; panel của bối cảnh lấy kết quả khi `row` về. Id ngoài plan không bao giờ được đọc sớm.
 */
import { peekWorkflowHandoff, startWorkflowHandoff, workflowHandoffGeneration } from '../../services/api/workflowReadHandoff';

export const AK = {
  plans: (storyId: string) => `plans:${storyId}`,
  plan: (planId: string) => `plan:${planId}`,
  chars: (storyId: string) => `characters:${storyId}`,
  looks: (storyId: string) => `looks:${storyId}`,
  sceneLooks: (planId: string) => `scene-looks:${planId}`,
  locations: (storyId: string) => `locations:${storyId}`,
  props: (storyId: string) => `props:${storyId}`,
  bankUsage: (planId: string) => `bank-usage:${planId}`,
  castCandidates: (planId: string) => `cast-candidates:${planId}`,
  needs: (planId: string) => `location-needs:${planId}`,
  /** `assets/preview` ≡ `steps/ASSETS/subjects` (cùng `getSubjects(plan, ASSETS)` ở server) ⇒ MỘT khoá, MỘT lượt đọc cho cả hai consumer. */
  assets: (planId: string) => `assets-subjects:${planId}`,
  style: (planId: string) => `style-profile:${planId}`,
  world: (planId: string) => `world-setting:${planId}`,
  stale: (planId: string) => `world-stale:${planId}`,
  models: () => 'image-studio-models',
  locDirections: (planId: string, locationId: string) => `loc-directions:${planId}:${locationId}`,
  locViews: (planId: string, locationId: string) => `loc-views:${planId}:${locationId}`,
  locList: (locationId: string) => `loc-list:${locationId}`,
} as const;

export interface AssetsPreloadApi {
  listPlans: (storyId: string) => Promise<any>; getPlan: (planId: string) => Promise<any>;
  listCharacters: (storyId: string) => Promise<any>; listLooks: (storyId: string) => Promise<any>; sceneLooks: (planId: string) => Promise<any>;
  listLocations: (storyId: string) => Promise<any>; listProps: (storyId: string) => Promise<any>; bankUsage: (planId: string) => Promise<any>; castCandidates: (planId: string) => Promise<any>;
  getLocationNeeds: (planId: string) => Promise<any>; getAssetsPreview: (planId: string) => Promise<any>;
  getStyleProfile: (planId: string) => Promise<any>; getWorldSetting: (planId: string) => Promise<any>; getStaleAssets: (planId: string) => Promise<any>; imageStudioModels: () => Promise<any>;
  getWorkflowLocationDirections: (planId: string, locationId: string) => Promise<any>;
  getWorkflowLocationViews: (planId: string, locationId?: string) => Promise<any>;
  listLocationViews: (locationId: string) => Promise<any>;
}

/** Số GET ảnh hướng đọc sớm cùng lúc tối đa (BE tính CPU cho từng GET; không bắn hàng chục GET một lượt). */
export const EARLY_LOCATION_READ_POOL = 4;

/** Bối cảnh THUỘC plan: id trong `bank-usage.locationIds` VÀ có căn cứ lọc (`filterable.locations`). Không chắc ⇒ rỗng (không đọc sớm). Giữ thứ tự của `locations`. */
export function locationIdsInPlan(locations: Array<{ id: string }> | null | undefined, usage: { locationIds?: string[]; filterable?: { locations?: boolean } } | null | undefined): string[] {
  if (!locations?.length || !usage || usage.filterable?.locations !== true || !Array.isArray(usage.locationIds)) return [];
  const inPlan = new Set(usage.locationIds);
  return locations.filter((l) => inPlan.has(l.id)).map((l) => l.id);
}

const createPool = (limit: number) => {
  let active = 0; const waiting: Array<() => void> = [];
  return async function run<T>(fn: () => Promise<T>): Promise<T> {
    if (active >= limit) await new Promise<void>((resolve) => waiting.push(resolve)); else active += 1;
    try { return await fn(); } finally { const next = waiting.shift(); if (next) next(); else active -= 1; }
  };
};

const sharedPool = createPool(EARLY_LOCATION_READ_POOL);

/** F2: đọc sớm ba GET ảnh hướng cho từng bối cảnh THUỘC plan. Idempotent theo khoá (đã có lượt hợp lệ ⇒ không bắn thêm). Gọi trước hay sau `location-needs` đều được. */
export function startLocationReads(planId: string, locationIds: string[], api: Pick<AssetsPreloadApi, 'getWorkflowLocationDirections' | 'getWorkflowLocationViews' | 'listLocationViews'>, pool = sharedPool): void {
  for (const id of locationIds) {
    startWorkflowHandoff(AK.locViews(planId, id), () => pool(() => api.getWorkflowLocationViews(planId, id)));
    startWorkflowHandoff(AK.locDirections(planId, id), () => pool(() => api.getWorkflowLocationDirections(planId, id)));
    startWorkflowHandoff(AK.locList(id), () => pool(() => api.listLocationViews(id)));
  }
}

/** F1: bắn cùng lúc mọi GET Tài sản không phụ thuộc nhau; rồi nối F2 ngay khi `locations` + `bank-usage` về (không chờ component, không chờ location-needs). */
export function startAssetsPreload(planId: string, storyId: string, api: AssetsPreloadApi): void {
  if (!planId || !storyId) return;
  startWorkflowHandoff(AK.plans(storyId), () => api.listPlans(storyId));
  startWorkflowHandoff(AK.plan(planId), () => api.getPlan(planId));
  startWorkflowHandoff(AK.chars(storyId), () => api.listCharacters(storyId));
  startWorkflowHandoff(AK.looks(storyId), () => api.listLooks(storyId));
  startWorkflowHandoff(AK.sceneLooks(planId), () => api.sceneLooks(planId));
  startWorkflowHandoff(AK.locations(storyId), () => api.listLocations(storyId), 2);       // useBankPrep + FilmLocationBank
  startWorkflowHandoff(AK.props(storyId), () => api.listProps(storyId));
  startWorkflowHandoff(AK.bankUsage(planId), () => api.bankUsage(planId), 2);              // useBankPrep + useBankUsage (FilmLocationBank)
  startWorkflowHandoff(AK.castCandidates(planId), () => api.castCandidates(planId));
  startWorkflowHandoff(AK.needs(planId), () => api.getLocationNeeds(planId));
  startWorkflowHandoff(AK.assets(planId), () => api.getAssetsPreview(planId), 2);          // initialRead của useWorkflowStepActions + WorkflowAssetsTasksPanel
  startWorkflowHandoff(AK.style(planId), () => api.getStyleProfile(planId));
  startWorkflowHandoff(AK.world(planId), () => api.getWorldSetting(planId));
  startWorkflowHandoff(AK.stale(planId), () => api.getStaleAssets(planId));
  startWorkflowHandoff(AK.models(), () => api.imageStudioModels());
  const generation = workflowHandoffGeneration();
  void Promise.all([peekWorkflowHandoff<any[]>(AK.locations(storyId)), peekWorkflowHandoff<any>(AK.bankUsage(planId))])
    .then(([locations, usage]) => { if (generation !== workflowHandoffGeneration()) return; const ids = locationIdsInPlan(locations, usage); if (ids.length) startLocationReads(planId, ids, api); })
    .catch(() => undefined);
}
